import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
} from '@mui/material';
import {useValidateDestination} from '../../../hooks/productions/useProductions';
import {ValidateDestinationRequestParams} from '../../../api/types/production.type';
import '../Demo.scss';

const ValidateDestinationApiTest: React.FC = () => {
  const [rootPath, setRootPath] = useState<string>(
    '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\Productions\\dndproject8'
  );
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const requestParams: ValidateDestinationRequestParams = {
    rootPath: rootPath,
  };

  const {
    data: validateData,
    isLoading,
    error,
    refetch,
  } = useValidateDestination(requestParams, enabled);

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
  };

  const handleCopyJson = async () => {
    if (validateData) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(validateData, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Validate Destination API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Validate a production root path. Checks if the path exists and returns
          validation status with a message.
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" data-testid={`validate-destination-api-chip-info`} />
            <Typography variant="h6" className="typo-text subhead2_size">
              Validate Destination
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/destination/validate
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Validate a production root path. Returns success status, whether the
            path already exists, and a descriptive message.
          </Typography>
        </CardContent>
      </Card>
      {/* Request Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Query Parameters
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label="Root Path"
              value={rootPath}
              onChange={(e) => setRootPath(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g., \\\\server\\share\\path"
              helperText="Use double backslashes for network paths"
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTestApi}
              data-testid="validate-destination-api-test-test-api-button">
              🚀 Test API
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              data-testid="validate-destination-api-test-reset-button">
              🔄 Reset
            </Button>
            {enabled && (
              <Button
                variant="outlined"
                onClick={() => refetch()}
                data-testid="validate-destination-api-test-refetch-button">
                ♻️ Refetch
              </Button>
            )}
            {validateData && (
              <Button
                variant="outlined"
                onClick={handleCopyJson}
                data-testid="validate-destination-api-test-button">
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* Loading State */}
      {isLoading && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">
              ⏳ Loading... Validating destination...
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Error State */}
      {error && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {error.message ||
                  'An error occurred while validating destination'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Response Data */}
      {validateData && !isLoading && (
        <>
          <Card sx={{marginBottom: 3}}>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Validation Result
              </Typography>

              <Box sx={{marginBottom: 2}}>
                <Alert
                  severity={validateData.success ? 'success' : 'warning'}
                  sx={{marginBottom: 2}}
                >
                  <Typography className="typo-text subhead3_size" gutterBottom>
                    {validateData.success ? '✅ Success' : '⚠️ Warning'}
                  </Typography>
                  <Typography className="typo-text body3_size">
                    {validateData.message}
                  </Typography>
                </Alert>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    marginTop: 2,
                  }}
                >
                  <Chip
                    label={`Success: ${validateData.success ? 'Yes' : 'No'}`}
                    color={validateData.success ? 'success' : 'default'}
                    size="small"
                    data-testid={`validate-destination-api-chip-success`}
                  />
                  <Chip
                    label={`Already Exists: ${validateData.alreadyExists ? 'Yes' : 'No'}`}
                    color={validateData.alreadyExists ? 'warning' : 'default'}
                    size="small"
                    data-testid={`validate-destination-api-chip-already-exists`}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* JSON Response */}
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(validateData, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default ValidateDestinationApiTest;
